import { useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ImportsPage from '../../ImportsPage';
import type { IntegrationsTab } from '../../types/index';
import { useLegacyAppSession } from '../../contexts/LegacyAppSessionContext';
import { useImportsHistory } from '../../hooks/useImports';
import { getAvailableActions, getResourceActions } from '../../utils/permissionCatalog';
import { checkPermissionActions } from '../../utils/permissions';

export default function ImportsContainer() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { org, currentUser } = useLegacyAppSession();
  const actions = getAvailableActions(currentUser);
  const canReadImports = checkPermissionActions(actions, getResourceActions('Imports'));
  const canReadFacilities = checkPermissionActions(actions, getResourceActions('Facilities'));
  const defaultTab: IntegrationsTab = canReadImports ? 'history' : 'facilities';

  const activeTab = useMemo<IntegrationsTab>(() => {
    const tab = searchParams.get('tab');
    if (tab === 'facilities' && canReadFacilities) return 'facilities';
    if (tab === 'history' && canReadImports) return 'history';
    return defaultTab;
  }, [canReadFacilities, canReadImports, defaultTab, searchParams]);
  useEffect(() => {
    if (searchParams.get('tab') !== activeTab) {
      setSearchParams({ tab: activeTab }, { replace: true });
    }
  }, [activeTab, searchParams, setSearchParams]);
  const history = useImportsHistory({
    organizationId: org?.id ?? null,
    enabled: activeTab === 'history',
  });

  return (
    <ImportsPage
      organizationId={org?.id ?? null}
      isQuickstartOrganization={org?.isQuickstart === true}
      currentUser={currentUser}
      history={history.results}
      historyError={history.error}
      historyLoading={history.isLoading}
      onAdd={() => navigate('/imports/metrc/auth', { state: { returnTo: '/imports', workflow: 'integration' } })}
      onPageChange={history.changePage}
      onSort={history.toggleSort}
      onView={(job) => navigate(`/imports/jobs/${job.id}`, { state: { job } })}
      onFacilityImport={() => navigate('/imports/metrc/auth', { state: { returnTo: '/imports?tab=history', workflow: 'facilities' } })}
      onFacilityEdit={(facility) =>
        navigate(`/imports/facilities/${facility.id}/edit?tab=facilities`, { state: { facility } })
      }
      activeTab={activeTab}
      onTabChange={(tab) => {
        if ((tab === 'history' && canReadImports) || (tab === 'facilities' && canReadFacilities)) {
          setSearchParams({ tab });
        }
      }}
      sort={history.sort}
    />
  );
}
